import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  FaEnvelope, FaLock, FaEye, FaEyeSlash,
  FaShieldAlt, FaSignInAlt, FaGraduationCap,
  FaCheckCircle, FaArrowRight, FaKey, FaWhatsapp
} from 'react-icons/fa';

const API = 'https://sistemafinanciero.digitaleseduproperu.com/api';

export default function LoginPage() {
  const navigate  = useNavigate();
  const { login } = useAuth();

  // paso: 1=credenciales, 2=2FA, 3=olvido-email, 4=olvido-codigo
  const [paso,         setPaso]         = useState(1);
  const [email,        setEmail]        = useState('');
  const [password,     setPassword]     = useState('');
  const [codigo,       setCodigo]       = useState('');
  const [usuarioId,    setUsuarioId]    = useState('');
  const [emailMask,    setEmailMask]    = useState('');
  const [verPass,      setVerPass]      = useState(false);
  const [error,        setError]        = useState('');
  const [exito,        setExito]        = useState('');
  const [cargando,     setCargando]     = useState(false);
  const [contador,     setContador]     = useState(600);
  const [emailOlvido,  setEmailOlvido]  = useState('');
  const [codigoReset,  setCodigoReset]  = useState('');
  const [nuevaPass,    setNuevaPass]    = useState('');
  const [confirmaPass, setConfirmaPass] = useState('');
  const [verNuevaPass, setVerNuevaPass] = useState(false);

  useEffect(() => {
    if (paso !== 2 || contador <= 0) return;
    const t = setTimeout(() => setContador(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [paso, contador]);

  const min = String(Math.floor(contador / 60)).padStart(2, '0');
  const seg = String(contador % 60).padStart(2, '0');

  const handleCredenciales = async (e) => {
    e.preventDefault();
    setError(''); setCargando(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`,
        { email, password }, { headers: { 'Content-Type': 'application/json' } }
      );
      setUsuarioId(data.data.usuario_id);
      setEmailMask(data.data.email);
      setContador(600); setPaso(2);
    } catch (err) {
      setError(err.response?.data?.message || 'Correo o contraseña incorrectos.');
    } finally { setCargando(false); }
  };

  const handleCodigo = async () => {
    if (codigo.length !== 6) { setError('Ingresa los 6 dígitos.'); return; }
    if (contador === 0) { setError('El código expiró.'); return; }
    setError(''); setCargando(true);
    try {
      const { data } = await axios.post(`${API}/auth/verificar-codigo`,
        { usuario_id: usuarioId, codigo }, { headers: { 'Content-Type': 'application/json' } }
      );
      await login(data.data.token, data.data.usuario);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.errors?.codigo?.[0] || 'Código inválido o expirado.');
    } finally { setCargando(false); }
  };

  const handleOlvidoEmail = async (e) => {
    e.preventDefault();
    setError(''); setExito(''); setCargando(true);
    try {
      await axios.post(`${API}/auth/olvido-password`,
        { email: emailOlvido }, { headers: { 'Content-Type': 'application/json' } }
      );
      setExito('Si el correo existe, recibirás un código en los próximos minutos.');
      setPaso(4);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al enviar el código.');
    } finally { setCargando(false); }
  };

  const handleCambiarPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (nuevaPass !== confirmaPass) { setError('Las contraseñas no coinciden.'); return; }
    if (nuevaPass.length < 8) { setError('La contraseña debe tener al menos 8 caracteres.'); return; }
    setCargando(true);
    try {
      await axios.post(`${API}/auth/cambiar-password`,
        { email: emailOlvido, codigo: codigoReset, nueva_password: nuevaPass },
        { headers: { 'Content-Type': 'application/json' } }
      );
      setExito('¡Contraseña actualizada! Ya puedes iniciar sesión.');
      setTimeout(() => { setPaso(1); setExito(''); setEmailOlvido(''); setCodigoReset(''); setNuevaPass(''); setConfirmaPass(''); }, 2500);
    } catch (err) {
      setError(err.response?.data?.errors?.codigo?.[0] || 'Código inválido o expirado.');
    } finally { setCargando(false); }
  };

  const inputStyle = {
    width: '100%', padding: '12px 14px 12px 42px',
    border: '1.5px solid #E2E8F0', borderRadius: 10,
    fontSize: 14, outline: 'none', transition: 'all .2s',
    fontFamily: 'Inter, sans-serif', color: '#0F172A',
    background: '#F8FAFC',
  };

  const btnPrimary = (disabled) => ({
    width: '100%', padding: '13px',
    background: disabled ? '#94A3B8' : 'linear-gradient(135deg, #FF7A00 0%, #E55A00 100%)',
    color: '#fff', border: 'none', borderRadius: 10,
    fontWeight: 700, fontSize: 14, cursor: disabled ? 'not-allowed' : 'pointer',
    boxShadow: disabled ? 'none' : '0 6px 20px rgba(255,122,0,0.35)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    fontFamily: 'Inter, sans-serif', transition: 'all .2s', marginTop: 8,
    letterSpacing: 0.3,
  });

  const labelStyle = {
    fontSize: 11, fontWeight: 700, color: '#374151',
    display: 'block', marginBottom: 6,
    textTransform: 'uppercase', letterSpacing: 0.8,
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      fontFamily: 'Inter, sans-serif', background: '#F4F6FB',
    }}>

      {/* ── Panel izquierdo azul ── */}
      <div style={{
        background: 'linear-gradient(160deg, #0A1128 0%, #001B5C 50%, #0A2A6E 100%)',
        display: 'flex', flexDirection: 'column',
        justifyContent: 'center', alignItems: 'flex-start',
        padding: '60px 64px', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position:'absolute', top:'-80px', right:'-80px', width:400, height:400, background:'radial-gradient(circle, rgba(255,122,0,0.12) 0%, transparent 65%)', pointerEvents:'none' }} />
        <div style={{ position:'absolute', bottom:'-60px', left:'-60px', width:300, height:300, background:'radial-gradient(circle, rgba(10,42,110,0.6) 0%, transparent 65%)', pointerEvents:'none' }} />

        <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:48 }}>
          <div style={{ width:52, height:52, borderRadius:14, background:'linear-gradient(135deg, #FF7A00, #FF9A3C)', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 8px 24px rgba(255,122,0,0.4)' }}>
            <FaGraduationCap size={26} color="#fff" />
          </div>
          <div>
            <div style={{ fontSize:13, color:'rgba(255,255,255,0.45)', letterSpacing:3, textTransform:'uppercase', fontWeight:600 }}>Sistema Administrativo</div>
            <div style={{ fontSize:22, fontWeight:900, color:'#fff', letterSpacing:-0.5, lineHeight:1.1 }}>
              Edu<span style={{ color:'#FF7A00' }}>Financiero</span>
            </div>
          </div>
        </div>

        <div style={{ marginBottom:28 }}>
          <div style={{ display:'inline-block', background:'rgba(255,122,0,0.15)', border:'1px solid rgba(255,122,0,0.3)', borderRadius:20, padding:'4px 14px', fontSize:11, fontWeight:700, color:'#FF9A3C', letterSpacing:2, textTransform:'uppercase', marginBottom:20 }}>
            SIADEF · 2026
          </div>
          <h1 style={{ fontSize:44, fontWeight:900, color:'#fff', lineHeight:1.1, marginBottom:20, letterSpacing:-1 }}>
            Gestión<br /><span style={{ color:'#FF7A00' }}>financiera</span><br />inteligente
          </h1>
          <p style={{ fontSize:15, color:'rgba(255,255,255,0.62)', lineHeight:1.7, maxWidth:400, fontWeight:400 }}>
            Plataforma integral para la gestión administrativa, financiera y contable de tu institución educativa.
          </p>
        </div>

        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          {['Control total de ingresos y gastos','Reportes financieros en tiempo real','Gestión de planillas y honorarios'].map((f,i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:10 }}>
              <FaCheckCircle size={14} color="#FF7A00" />
              <span style={{ fontSize:14, color:'rgba(255,255,255,0.72)', fontWeight:500 }}>{f}</span>
            </div>
          ))}
        </div>

        <div style={{ position:'absolute', bottom:32, left:64, display:'flex', alignItems:'center', gap:8 }}>
          <FaShieldAlt size={13} color="rgba(255,255,255,0.35)" />
          <span style={{ fontSize:12, color:'rgba(255,255,255,0.35)', fontWeight:500 }}>Seguro, confiable y siempre disponible</span>
        </div>
      </div>

      {/* ── Panel derecho blanco ── */}
      <div style={{ background:'#fff', display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center', padding:'48px 52px', boxShadow:'-8px 0 32px rgba(0,0,0,0.06)' }}>
        <div style={{ width:'100%', maxWidth:360 }}>

          {/* PASO 1 — Credenciales */}
          {paso === 1 && (
            <>
              <div style={{ fontSize:11, fontWeight:700, color:'#94A3B8', letterSpacing:2.5, textTransform:'uppercase', marginBottom:10 }}>BIENVENIDO</div>
              <h2 style={{ fontSize:26, fontWeight:800, color:'#0F172A', marginBottom:6, letterSpacing:-0.5 }}>
                Inicia <span style={{ color:'#FF7A00' }}>sesión</span>
              </h2>
              <p style={{ fontSize:13, color:'#64748B', marginBottom:32, lineHeight:1.6 }}>Ingresa tus credenciales para continuar</p>

              <form onSubmit={handleCredenciales} noValidate>
                <div style={{ marginBottom:18 }}>
                  <label style={labelStyle}>Correo electrónico</label>
                  <div style={{ position:'relative' }}>
                    <FaEnvelope size={14} color="#94A3B8" style={{ position:'absolute', left:14, top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }} />
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="correo@institución.edu" style={inputStyle}
                      onFocus={e => e.target.style.borderColor='#FF7A00'} onBlur={e => e.target.style.borderColor='#E2E8F0'} required />
                  </div>
                </div>

                <div style={{ marginBottom:14 }}>
                  <label style={labelStyle}>Contraseña</label>
                  <div style={{ position:'relative' }}>
                    <FaLock size={13} color="#94A3B8" style={{ position:'absolute', left:14, top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }} />
                    <input type={verPass?'text':'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••"
                      style={{ ...inputStyle, paddingRight:42 }}
                      onFocus={e => e.target.style.borderColor='#FF7A00'} onBlur={e => e.target.style.borderColor='#E2E8F0'} required />
                    <span onClick={() => setVerPass(!verPass)} style={{ position:'absolute', right:14, top:'50%', transform:'translateY(-50%)', cursor:'pointer', color:'#94A3B8' }}>
                      {verPass ? <FaEyeSlash size={14}/> : <FaEye size={14}/>}
                    </span>
                  </div>
                </div>

                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
                  <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'#64748B', cursor:'pointer' }}>
                    <input type="checkbox" style={{ accentColor:'#FF7A00' }} /> Recordarme
                  </label>
                  <span onClick={() => { setPaso(3); setError(''); setExito(''); }}
                    style={{ fontSize:12, color:'#FF7A00', fontWeight:600, cursor:'pointer', textDecoration:'underline' }}>
                    ¿Olvidaste tu contraseña?
                  </span>
                </div>

                {error && <div style={{ background:'#FEE2E2', color:'#B91C1C', borderRadius:8, padding:'10px 14px', fontSize:12, marginBottom:16, fontWeight:500 }}>{error}</div>}

                <button type="submit" disabled={cargando} style={btnPrimary(cargando)}>
                  {cargando ? <><span className="spinner-border spinner-border-sm" style={{ width:14, height:14, borderWidth:2 }} />Verificando...</> : <><FaSignInAlt size={14}/>Iniciar sesión<FaArrowRight size={12}/></>}
                </button>
              </form>

              <div style={{ textAlign:'center', marginTop:20 }}>
                <p style={{ fontSize:12, color:'#94A3B8', marginBottom:8 }}>
                  ¿No tienes acceso? Contacta al administrador
                </p>
                <a href="tel:+51960183250" style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:13, color:'#25D366', fontWeight:700, textDecoration:'none', background:'rgba(37,211,102,0.08)', padding:'8px 16px', borderRadius:8, border:'1px solid rgba(37,211,102,0.2)' }}>
                  <FaWhatsapp size={16}/> +51 960 183 250
                </a>
              </div>
            </>
          )}

          {/* PASO 2 — Verificación 2FA */}
          {paso === 2 && (
            <>
              <div style={{ textAlign:'center', marginBottom:28 }}>
                <div style={{ width:56, height:56, borderRadius:'50%', background:'linear-gradient(135deg, #FF7A00, #FF9A3C)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px', boxShadow:'0 8px 24px rgba(255,122,0,0.35)' }}>
                  <FaShieldAlt size={24} color="#fff" />
                </div>
                <h2 style={{ fontSize:22, fontWeight:800, color:'#0F172A', marginBottom:6, letterSpacing:-0.4 }}>
                  Verificación <span style={{ color:'#FF7A00' }}>2FA</span>
                </h2>
                <p style={{ fontSize:13, color:'#64748B', lineHeight:1.6 }}>
                  Código enviado a <strong style={{ color:'#0F172A' }}>{emailMask}</strong>
                </p>
              </div>

              <div style={{ marginBottom:20 }}>
                <label style={labelStyle}>Código de verificación</label>
                <input type="text" value={codigo} maxLength={6} autoFocus
                  onChange={e => setCodigo(e.target.value.replace(/\D/g,'').slice(0,6))}
                  placeholder="000000"
                  style={{ ...inputStyle, paddingLeft:14, fontSize:28, fontWeight:800, textAlign:'center', letterSpacing:12 }}
                  onFocus={e => e.target.style.borderColor='#FF7A00'} onBlur={e => e.target.style.borderColor='#E2E8F0'} />
                <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:8, marginTop:10 }}>
                  <div style={{ background:contador<60?'#FEE2E2':'#F1F5F9', border:`1px solid ${contador<60?'#FECACA':'#E2E8F0'}`, borderRadius:8, padding:'4px 14px', fontSize:13, fontWeight:700, color:contador<60?'#EF4444':'#64748B' }}>
                    ⏱ {min}:{seg}
                  </div>
                  <span style={{ fontSize:12, color:'#94A3B8' }}>restantes</span>
                </div>
              </div>

              {error && <div style={{ background:'#FEE2E2', color:'#B91C1C', borderRadius:8, padding:'10px 14px', fontSize:12, marginBottom:16, fontWeight:500 }}>{error}</div>}

              <button type="button" disabled={cargando||contador===0} onClick={handleCodigo} style={btnPrimary(cargando||contador===0)}>
                {cargando ? <><span className="spinner-border spinner-border-sm" style={{ width:14, height:14, borderWidth:2 }}/>Verificando...</> : <><FaShieldAlt size={14}/>Verificar e ingresar</>}
              </button>
              <button type="button" onClick={() => { setPaso(1); setError(''); setCodigo(''); }}
                style={{ width:'100%', padding:'11px', background:'transparent', color:'#94A3B8', border:'1.5px solid #E2E8F0', borderRadius:10, fontWeight:500, fontSize:13, cursor:'pointer', marginTop:10, fontFamily:'Inter,sans-serif' }}>
                ← Volver
              </button>
            </>
          )}

          {/* PASO 3 — Olvidé mi contraseña (ingresar email) */}
          {paso === 3 && (
            <>
              <div style={{ textAlign:'center', marginBottom:28 }}>
                <div style={{ width:56, height:56, borderRadius:'50%', background:'linear-gradient(135deg, #FF7A00, #FF9A3C)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px', boxShadow:'0 8px 24px rgba(255,122,0,0.35)' }}>
                  <FaKey size={22} color="#fff" />
                </div>
                <h2 style={{ fontSize:22, fontWeight:800, color:'#0F172A', marginBottom:6, letterSpacing:-0.4 }}>
                  Recuperar <span style={{ color:'#FF7A00' }}>contraseña</span>
                </h2>
                <p style={{ fontSize:13, color:'#64748B', lineHeight:1.6 }}>
                  Ingresa tu correo y te enviaremos un código de recuperación
                </p>
              </div>

              <form onSubmit={handleOlvidoEmail} noValidate>
                <div style={{ marginBottom:20 }}>
                  <label style={labelStyle}>Correo electrónico</label>
                  <div style={{ position:'relative' }}>
                    <FaEnvelope size={14} color="#94A3B8" style={{ position:'absolute', left:14, top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }} />
                    <input type="email" value={emailOlvido} onChange={e => setEmailOlvido(e.target.value)} placeholder="correo@institución.edu"
                      style={inputStyle} onFocus={e => e.target.style.borderColor='#FF7A00'} onBlur={e => e.target.style.borderColor='#E2E8F0'} required />
                  </div>
                </div>

                {error && <div style={{ background:'#FEE2E2', color:'#B91C1C', borderRadius:8, padding:'10px 14px', fontSize:12, marginBottom:16, fontWeight:500 }}>{error}</div>}
                {exito && <div style={{ background:'#DCFCE7', color:'#15803D', borderRadius:8, padding:'10px 14px', fontSize:12, marginBottom:16, fontWeight:500 }}>{exito}</div>}

                <button type="submit" disabled={cargando} style={btnPrimary(cargando)}>
                  {cargando ? <><span className="spinner-border spinner-border-sm" style={{ width:14, height:14, borderWidth:2 }}/>Enviando...</> : <><FaEnvelope size={14}/>Enviar código</>}
                </button>
              </form>
              <button type="button" onClick={() => { setPaso(1); setError(''); setExito(''); }}
                style={{ width:'100%', padding:'11px', background:'transparent', color:'#94A3B8', border:'1.5px solid #E2E8F0', borderRadius:10, fontWeight:500, fontSize:13, cursor:'pointer', marginTop:10, fontFamily:'Inter,sans-serif' }}>
                ← Volver al login
              </button>
            </>
          )}

          {/* PASO 4 — Ingresar código y nueva contraseña */}
          {paso === 4 && (
            <>
              <div style={{ textAlign:'center', marginBottom:24 }}>
                <div style={{ width:56, height:56, borderRadius:'50%', background:'linear-gradient(135deg, #FF7A00, #FF9A3C)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px', boxShadow:'0 8px 24px rgba(255,122,0,0.35)' }}>
                  <FaKey size={22} color="#fff" />
                </div>
                <h2 style={{ fontSize:22, fontWeight:800, color:'#0F172A', marginBottom:6, letterSpacing:-0.4 }}>
                  Nueva <span style={{ color:'#FF7A00' }}>contraseña</span>
                </h2>
                <p style={{ fontSize:13, color:'#64748B', lineHeight:1.6 }}>
                  Ingresa el código recibido en <strong>{emailOlvido}</strong>
                </p>
              </div>

              <form onSubmit={handleCambiarPassword} noValidate>
                <div style={{ marginBottom:16 }}>
                  <label style={labelStyle}>Código de recuperación</label>
                  <input type="text" value={codigoReset} maxLength={6}
                    onChange={e => setCodigoReset(e.target.value.replace(/\D/g,'').slice(0,6))}
                    placeholder="000000"
                    style={{ ...inputStyle, paddingLeft:14, fontSize:22, fontWeight:800, textAlign:'center', letterSpacing:8 }}
                    onFocus={e => e.target.style.borderColor='#FF7A00'} onBlur={e => e.target.style.borderColor='#E2E8F0'} />
                </div>

                <div style={{ marginBottom:16 }}>
                  <label style={labelStyle}>Nueva contraseña</label>
                  <div style={{ position:'relative' }}>
                    <FaLock size={13} color="#94A3B8" style={{ position:'absolute', left:14, top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }} />
                    <input type={verNuevaPass?'text':'password'} value={nuevaPass}
                      onChange={e => setNuevaPass(e.target.value)} placeholder="Mínimo 8 caracteres"
                      style={{ ...inputStyle, paddingRight:42 }}
                      onFocus={e => e.target.style.borderColor='#FF7A00'} onBlur={e => e.target.style.borderColor='#E2E8F0'} required />
                    <span onClick={() => setVerNuevaPass(!verNuevaPass)} style={{ position:'absolute', right:14, top:'50%', transform:'translateY(-50%)', cursor:'pointer', color:'#94A3B8' }}>
                      {verNuevaPass ? <FaEyeSlash size={14}/> : <FaEye size={14}/>}
                    </span>
                  </div>
                </div>

                <div style={{ marginBottom:20 }}>
                  <label style={labelStyle}>Confirmar contraseña</label>
                  <div style={{ position:'relative' }}>
                    <FaLock size={13} color="#94A3B8" style={{ position:'absolute', left:14, top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }} />
                    <input type="password" value={confirmaPass}
                      onChange={e => setConfirmaPass(e.target.value)} placeholder="Repite la contraseña"
                      style={inputStyle}
                      onFocus={e => e.target.style.borderColor='#FF7A00'} onBlur={e => e.target.style.borderColor='#E2E8F0'} required />
                  </div>
                </div>

                {error && <div style={{ background:'#FEE2E2', color:'#B91C1C', borderRadius:8, padding:'10px 14px', fontSize:12, marginBottom:16, fontWeight:500 }}>{error}</div>}
                {exito && <div style={{ background:'#DCFCE7', color:'#15803D', borderRadius:8, padding:'10px 14px', fontSize:12, marginBottom:16, fontWeight:500 }}>{exito}</div>}

                <button type="submit" disabled={cargando} style={btnPrimary(cargando)}>
                  {cargando ? <><span className="spinner-border spinner-border-sm" style={{ width:14, height:14, borderWidth:2 }}/>Actualizando...</> : <><FaKey size={14}/>Cambiar contraseña</>}
                </button>
              </form>
              <button type="button" onClick={() => { setPaso(3); setError(''); setExito(''); }}
                style={{ width:'100%', padding:'11px', background:'transparent', color:'#94A3B8', border:'1.5px solid #E2E8F0', borderRadius:10, fontWeight:500, fontSize:13, cursor:'pointer', marginTop:10, fontFamily:'Inter,sans-serif' }}>
                ← Volver
              </button>
            </>
          )}

          <div style={{ textAlign:'center', marginTop:32, paddingTop:20, borderTop:'1px solid #F1F5F9' }}>
            <div style={{ fontSize:11, color:'#CBD5E1', fontWeight:500 }}>Digitales EduPro · SIADEF 2026</div>
          </div>
        </div>
      </div>
    </div>
  );
}